export const money = (value) => Math.round(((Number(value) || 0) + Number.EPSILON) * 100) / 100;
export const formatMoney = (value) =>
  money(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const parseJson = (value, fallback) => {
  if (Array.isArray(value) || (value && typeof value === 'object')) return value;
  try {
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
};

export const splitEmails = (input) =>
  [...new Set(String(input || '').split(/[;,\n]/).map((email) => email.trim().toLowerCase()).filter(Boolean))];

export const isValidEmail = (email) => /^[^\s@,;<>"\\]+@[^\s@,;<>"\\]+\.[^\s@,;<>"\\]+$/.test(email);

export const lineAmount = (item) => {
  if (item.billing_basis === 'cleaning_visits') {
    return money((Number(item.qty) || 0) * (Number(item.unit_price) || 0));
  }
  if (item.type === 'credit') return -money(Math.abs(Number(item.amount) || 0));
  if (item.qty !== undefined && item.unit_price !== undefined && item.qty !== '' && item.unit_price !== '') {
    return money((Number(item.qty) || 0) * (Number(item.unit_price) || 0));
  }
  return money(item.amount);
};

export const cleaningServiceDates = (profile, month) => {
  if (!profile || !/^\d{4}-\d{2}$/.test(month || '')) return [];
  const [year, monthNumber] = month.split('-').map(Number);
  const last = new Date(year, monthNumber, 0).getDate();
  const monthStart = `${month}-01`;
  const monthEnd = `${month}-${String(last).padStart(2, '0')}`;
  if ((profile.start_date && profile.start_date > monthEnd) || (profile.end_date && profile.end_date < monthStart)) return [];
  const firstAllowed = profile.start_date && profile.start_date > monthStart ? Number(profile.start_date.slice(8, 10)) : 1;
  const lastAllowed = profile.end_date && profile.end_date < monthEnd ? Number(profile.end_date.slice(8, 10)) : last;
  const dates = [];
  if (profile.schedule_type === 'monthly') {
    (Array.isArray(profile.month_days) ? profile.month_days : []).forEach((day) => {
      const actual = Math.min(last, Math.max(1, Number(day)));
      if (actual >= firstAllowed && actual <= lastAllowed) dates.push(`${month}-${String(actual).padStart(2, '0')}`);
    });
  } else {
    const weekdays = Array.isArray(profile.weekdays) ? profile.weekdays.map(Number) : [];
    for (let day = 1; day <= last; day += 1) {
      const date = new Date(year, monthNumber - 1, day);
      if (day >= firstAllowed && day <= lastAllowed && weekdays.includes(date.getDay())) {
        dates.push(`${month}-${String(day).padStart(2, '0')}`);
      }
    }
  }
  return [...new Set(dates)].sort();
};

export function calculateTotals(groups, adjustments = {}) {
  let materialsSubtotal = 0;
  let laborSubtotal = 0;
  let creditSubtotal = 0;
  let taxableSubtotal = 0;
  groups.forEach((group) =>
    (group.items || []).forEach((item) => {
      const amount = lineAmount(item);
      if (item.type === 'credit') creditSubtotal += Math.abs(amount);
      else if (item.type === 'material') {
        materialsSubtotal += amount;
        if (item.taxable !== false) taxableSubtotal += Math.max(0, amount);
      } else {
        laborSubtotal += amount;
        if (item.taxable === true) taxableSubtotal += Math.max(0, amount);
      }
    }),
  );
  materialsSubtotal = money(materialsSubtotal);
  laborSubtotal = money(laborSubtotal);
  creditSubtotal = money(creditSubtotal);
  const base = money(materialsSubtotal + laborSubtotal - creditSubtotal);
  const discountRaw = Number(adjustments.discount_value) || 0;
  const discount = money(adjustments.discount_type === 'percent' ? base * discountRaw / 100 : discountRaw);
  const invoiceDiscount = discount;
  const feeRaw = Number(adjustments.fee_value) || 0;
  const fee = money(adjustments.fee_type === 'percent' ? Math.max(0, base - invoiceDiscount) * feeRaw / 100 : feeRaw);
  const taxableRatio = materialsSubtotal + laborSubtotal > 0
    ? taxableSubtotal / (materialsSubtotal + laborSubtotal)
    : 0;
  const adjustedTaxable = Math.max(0, taxableSubtotal - invoiceDiscount * taxableRatio);
  const taxRate = Math.max(0, Number(adjustments.tax_rate ?? 6.25) || 0);
  const tax = money(adjustedTaxable * taxRate / 100);
  return {
    materialsSubtotal,
    laborSubtotal,
    creditSubtotal,
    subtotal: base,
    discount: invoiceDiscount,
    fee,
    taxableSubtotal: money(adjustedTaxable),
    tax,
    total: money(base - invoiceDiscount + fee + tax),
  };
}
